import { useContext, useCallback, useRef } from 'react';

import type { ToastState } from '@sb/ui/components';
import { GlobalContext } from '@sbrc/contexts';
import { createNotification } from '@sbrc/services';

import useRobotList from './useRobotList';

const isMessageString = (message: React.ReactNode | string) => {
  return typeof message === 'string';
};

/**
 * How long the same toast message is treated as already recorded.
 *
 * Every toast writes a notification to feathers-bot. A burst of robot failures
 * (back-to-back collisions raise one per collision) otherwise turns into a
 * burst of writes on the same socket the rest of the app reads its data over —
 * including the feature-flag subscription a newly-mounted component is waiting
 * on. Repeats inside this window are shown but not re-recorded.
 */
const DUPLICATE_NOTIFICATION_WINDOW_MS = 2000;

const useToast = () => {
  const { toast } = useContext(GlobalContext);
  const robots = useRobotList();
  const { state, set } = toast;
  const robotID = robots?.[0]?.id;

  // Last recorded message and when, so a repeat of the same failure doesn't
  // write another notification. Per-hook rather than global: the messages worth
  // collapsing are the ones a single bridge re-fires.
  const lastNotificationRef = useRef<{ message: string; at: number } | null>(
    null,
  );

  const setToast = useCallback(
    (newToast: ToastState | null) => {
      if (newToast?.kind === 'error') {
        // eslint-disable-next-line no-console
        console.error({
          kind: 'toastError',
          newToast,
        });

        if (newToast.error) {
          // eslint-disable-next-line no-console
          console.error(newToast.error);
        } else {
          // eslint-disable-next-line no-console
          console.trace(`Toast error ${newToast.message}`);
        }
      }

      if (
        robotID &&
        newToast &&
        isMessageString(newToast?.message) &&
        !newToast.skipNotification
      ) {
        const message = newToast.message as string;
        const now = Date.now();
        const last = lastNotificationRef.current;

        const isRecentDuplicate =
          last?.message === message &&
          now - last.at < DUPLICATE_NOTIFICATION_WINDOW_MS;

        if (!isRecentDuplicate) {
          lastNotificationRef.current = { message, at: now };

          // Deliberately not awaited — the toast shows regardless. It does need
          // a handler: an unhandled rejection here used to be the normal
          // outcome whenever the write failed, which is exactly when the robot
          // is already in trouble.
          createNotification({
            kind: 'toast',
            robotID,
            failureReason: message,
            failureKind: newToast.kind,
          }).catch(() => {
            // Recording a notification is best-effort; losing one must not
            // take down the surface that reported the failure.
          });
        }
      }

      set(newToast);
    },
    [robotID, set],
  );

  return { toastState: state, setToast };
};

export default useToast;
